import { useMemo, useState } from "react";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import TableCell from "../../../../BillingAndPayments/components/TableCell";
import { expanderColumnSx, metricColumnSx } from "../columnWidths";
import { AD_GROUP } from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";

import { useFormContext } from "react-hook-form";
import GoogleKeywordRow from "./GoogleKeywordRow";
import { renderMetricValue } from "../../../../../utils/renderMetricValue";
import {
  ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION,
  ADS_GOOGLE_WEBPAGE_CRITERION_URL_EQUALS_OPTION,
} from "../../../../../utils/constants";

const SORT_METRIC_KEYS = {
  spend: "spend",
  impressions: "impressions",
  click: "click",
  ctr: "ctr",
  mainAction: "mainAction",
  costPerMainAction: "costPerMainAction",
};

const buildExpectedWebpageCriterionString = ({ operator, value }) => {
  if (!value) return null;
  if (operator === ADS_GOOGLE_WEBPAGE_CRITERION_URL_EQUALS_OPTION) {
    return `URL==${value}`;
  }
  return value;
};

const findDsaStatsForWebpage = (webpagesMap, wp) => {
  if (wp?.id && webpagesMap[wp.id] && typeof webpagesMap[wp.id] === "object") {
    return { stats: webpagesMap[wp.id], mapKey: wp.id };
  }
  const expected = buildExpectedWebpageCriterionString(wp);
  for (const [mapKey, stats] of Object.entries(webpagesMap)) {
    if (!stats || typeof stats !== "object") continue;
    if (expected && stats.webpage === expected) {
      return { stats, mapKey };
    }
  }
  if (wp?.value) {
    for (const [mapKey, stats] of Object.entries(webpagesMap)) {
      if (stats?.webpage?.includes(wp.value)) {
        return { stats, mapKey };
      }
    }
  }
  return { stats: {}, mapKey: wp?.id ?? "" };
};

const buildSortedChildRows = ({
  searchAdGroupType,
  foundAdGroup,
  childStatistics,
  sortConfig,
}) => {
  const isDsa =
    searchAdGroupType === ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION;

  let rows;

  if (!isDsa) {
    const keywords = foundAdGroup?.keywords || [];
    const keywordsIDs = foundAdGroup?.keywordsIDs || [];
    const map = childStatistics || {};

    rows = [...keywords].map((keyword, index) => ({
      rowKey: keywordsIDs[index],
      label: keyword,
      data: map[keywordsIDs[index]] || {},
    }));
  } else {
    const webpages = foundAdGroup?.webpages || [];
    const webpagesMap =
      childStatistics && typeof childStatistics === "object"
        ? childStatistics
        : {};

    if (webpages.length === 0) {
      rows = Object.entries(webpagesMap).map(([mapKey, stats]) => ({
        rowKey: mapKey,
        label: stats?.webpage || mapKey,
        data: stats && typeof stats === "object" ? stats : {},
      }));
    } else {
      rows = webpages.map((wp) => {
        const { stats, mapKey } = findDsaStatsForWebpage(webpagesMap, wp);
        const label = stats?.webpage || wp.value || mapKey || "";
        return {
          rowKey: mapKey || wp.id,
          label,
          data: stats,
        };
      });

      const usedKeys = new Set(rows.map((r) => r.rowKey).filter(Boolean));
      for (const [mapKey, stats] of Object.entries(webpagesMap)) {
        if (usedKeys.has(mapKey)) continue;
        if (!stats || typeof stats !== "object") continue;
        rows.push({
          rowKey: mapKey,
          label: stats.webpage || mapKey,
          data: stats,
        });
      }
    }
  }

  const metric = SORT_METRIC_KEYS[sortConfig?.key];
  if (!metric) return rows;

  return [...rows].sort((a, b) => {
    const comparison = (a.data[metric] || 0) - (b.data[metric] || 0);
    return sortConfig.direction === "asc" ? comparison : -comparison;
  });
};

const GoogleSearchAdGroupRow = ({
  indexCampaign,
  indexAdGroup,
  adGroupID,
  data,
  childStatistics,
  searchAdGroupType,
  sortConfig,
  columns,
}) => {
  const [open, setOpen] = useState(false);
  const { campaign } = useFormContext();
  const { t } = useTranslationApp();

  const adsGroups = campaign.adsGroups || [];
  const foundAdGroup = adsGroups?.find((adGroup) => adGroup.id === adGroupID);

  const sortedChildRows = useMemo(
    () =>
      buildSortedChildRows({
        searchAdGroupType,
        foundAdGroup,
        childStatistics,
        sortConfig,
      }),
    [searchAdGroupType, foundAdGroup, childStatistics, sortConfig]
  );

  return (
    <>
      <TableRow>
        <TableCell indexRow={indexCampaign} sx={expanderColumnSx} level={2}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        {columns.map((col) => (
          <TableCell
            key={col.key}
            indexRow={indexCampaign}
            level={2}
            sx={metricColumnSx(col.width)}
          >
            {(() => {
              switch (col.key) {
                case "platform":
                  return (
                    foundAdGroup?.name || `${t(AD_GROUP)} ${indexAdGroup + 1}`
                  );
                case "spend":
                  return renderMetricValue(
                    data.spend,
                    col.key,
                    data.currency
                  );
                case "impressions":
                  return renderMetricValue(
                    data.impressions,
                    col.key,
                    data.currency
                  );
                case "click":
                  return renderMetricValue(
                    data.click,
                    col.key,
                    data.currency
                  );
                case "ctr":
                  return renderMetricValue(data.ctr, col.key, data.currency);
                case "mainAction":
                  return renderMetricValue(
                    data.mainAction,
                    col.key,
                    data.currency
                  );
                case "costPerMainAction":
                  return renderMetricValue(
                    data.costPerMainAction,
                    col.key,
                    data.currency
                  );
                case "cpc":
                  return renderMetricValue(
                    (data.cpc || 0).toFixed(2),
                    col.key,
                    data.currency
                  );
                case "cpm":
                  return renderMetricValue(
                    (data.cpm || 0).toFixed(2),
                    col.key,
                    data.currency
                  );
                default:
                  return renderMetricValue(
                    data[col.key],
                    col.key,
                    data.currency
                  );
              }
            })()}
          </TableCell>
        ))}
      </TableRow>
      {open &&
        sortedChildRows.map(({ rowKey, label, data: rowData }, idx) => (
          <GoogleKeywordRow
            key={rowKey ? String(rowKey) : `${adGroupID}-child-${idx}`}
            indexCampaign={indexCampaign}
            keyword={label}
            data={rowData}
            columns={columns}
          />
        ))}
    </>
  );
};

export default GoogleSearchAdGroupRow;
